import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import FileUpload from "./FileUpload";
import EnvolvementPreviewModal from "./EnvolvementPreviewModal";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

const validCategories = [
  "Gaming",
  "Interactive",
  "Livestreamed",
  "Video",
  "Other",
  "Art",
  "Tutorials",
  "Travel",
  "Music",
  "Q&A",
  "Education",
];

function CreateEnvolvement() {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();

  const now = new Date();
  const today_year = now.getFullYear();
  const today_month = (now.getMonth() + 1).toString().padStart(2, '0');
  const today_day = now.getDate().toString().padStart(2, '0');
  const today = `${today_year}-${today_month}-${today_day}`;

  // Basic Information
  const [title, setTitle] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [description, setDescription] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [categories, setCategories] = useState([]);

  // Pricing & Availability
  const [price, setPrice] = useState(0);
  const [free, setFree] = useState(true);
  const [quantity, setQuantity] = useState(0);
  const [unlimited, setUnlimited] = useState(false);

  // Dates
  const [endDate, setEndDate] = useState(null);
  const [targetEnvolvmentDate, setTargetEnvolvmentDate] = useState(null);

  // Settings
  const [submissions, setSubmissions] = useState(false);
  const [submissionDescription, setSubmissionDescription] = useState("");
  const [submissionDeadline, setSubmissionDeadline] = useState(null);
  const [submissionContentPicture, setSubmissionContentPicture] =
    useState(false);
  const [submissionContentDescription, setSubmissionContentDescription] =
    useState(false);
  const [submissionContentUrl, setSubmissionContentUrl] = useState(false);
  const [comments, setComments] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedReleaseDate, setPublishedReleaseDate] = useState(null);

  // Form State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [postedID, setPostedID] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});

  const totalSteps = 4;

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    console.log("t");

    if (!title.trim()) newErrors.title = "Title is required";
    if (!previewText.trim()) newErrors.previewText = "Preview text is required";
    if (categories.length === 0)
      newErrors.categories = "At least one category is required";
    if (price < 0) newErrors.price = "Price cannot be negative";
    if (quantity < 0) newErrors.quantity = "Quantity cannot be negative";

    if (endDate && targetEnvolvmentDate && endDate > targetEnvolvmentDate) {
      newErrors.endDate = "End date cannot be after target envolvement date.";
      newErrors.targetEnvolvmentDate =
        "Target envolvement date cannot be before end date.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      title.trim() &&
      previewText.trim() &&
      categories.length > 0 &&
      price >= 0 &&
      quantity >= 0
    );
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return title.trim() && previewText.trim() && categories.length > 0;
      case 2:
        return price >= 0 && quantity >= 0;
      case 3:
        return true; // Dates are optional
      case 4:
        return true; // Settings are optional
      default:
        return false;
    }
  };

  const handleCategoryChange = (category) => {
    setCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((cat) => cat !== category);
      } else {
        if (prev.length < 3) {
          return [...prev, category];
        } else {
          // Maybe show a message to the user that they can only select 3.
          return prev;
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setMessage("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const accessToken = await getAccessTokenSilently();

      const envolvementData = {
        type: "BaseEnvolvement",
        isActive: false,
        userID: user?.sub,
        username: user?.nickname,
        userPicture: user?.picture,
        title: title.trim(),
        previewText: previewText.trim(),
        description: description.trim(),
        file: "",
        price: {
          price: free ? 0 : parseFloat(price),
          free: free,
        },
        quantity: {
          quantity: unlimited ? 0 : parseInt(quantity),
          unlimited: unlimited,
          currentEnvolves: 0,
        },
        categories: categories,
        endDate: {
          endDate: endDate,
          none: !endDate,
        },
        submissions: {
          submissionsAllowed: submissions,
          submissionsActive: false, // This will be activated in the next step if needed
          submissionsIDPair: null,
          submissionDescription: submissionDescription,
          submissionDeadline: submissionDeadline,
          submissionContent: {
            picture: submissionContentPicture,
            description: submissionContentDescription,
            url: submissionContentUrl,
          },
        },
        comments: comments,
        published: {
          published: published,
          publishedReleaseDate: publishedReleaseDate,
        },
        imgUrl: imgUrl,
        targetEnvolvmentDate: targetEnvolvmentDate,
        activityPostJSON: "",
      };

      const response = await fetch(
        `${backend_domain}${backend_port}/post/new-base-envolvement`,
        {
          method: "POST",
          body: JSON.stringify(envolvementData),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        const newPostedID = data.postedID;
        setPostedID(newPostedID);

        // If submissions were requested, open them now
        if (submissions) {
          const submissionData = {
            picture: submissionContentPicture,
            description: submissionContentDescription,
            url: submissionContentUrl,
          };
          const openSubmissionsRes = await fetch(
            `${backend_domain}${backend_port}/user_submissions/open-submissions/${newPostedID}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify(submissionData),
            }
          );

          if (!openSubmissionsRes.ok) {
            setMessage(
              "Envolvement created, but failed to open submissions. You can open them later by editing the Envolvement."
            );
          } else {
            setMessage("Envolvement created and submissions opened successfully!");
          }
        } else {
          setMessage("Envolvement created successfully!");
        }

        // Reset form
        setTitle("");
        setPreviewText("");
        setDescription("");
        setImgUrl("");
        setCategories([]);
        setPrice(0);
        setFree(true);
        setQuantity(0);
        setUnlimited(false);
        setEndDate(null);
        setTargetEnvolvmentDate(null);
        setSubmissions(false);
        setSubmissionDescription("");
        setSubmissionDeadline(null);
        setSubmissionContentPicture(false);
        setSubmissionContentDescription(false);
        setSubmissionContentUrl(false);
        setComments(false);
        setPublished(false);
        setPublishedReleaseDate(null);
        setErrors({});
      } else {
        setMessage("Error creating envolvement. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-center space-x-2 sm:space-x-4">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i + 1 < currentStep
                  ? "bg-green-500 text-white"
                  : i + 1 === currentStep
                  ? "bg-primary-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {i + 1 < currentStep ? "✓" : i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`w-8 sm:w-16 h-1 mx-2 ${
                  i + 1 < currentStep ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="text-center mt-2 text-sm text-gray-600">
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  );

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Basic Information
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength="200"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              placeholder="Enter your envolvement title"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.title ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview Text *
            </label>
            <textarea
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              maxLength="1000"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  e.preventDefault();
                }
              }}
              placeholder="Brief description that appears in previews"
              rows={3}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.previewText ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.previewText && (
              <p className="text-red-500 text-sm mt-1">{errors.previewText}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detailed Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength="4000"
              placeholder="Provide a detailed explanation of your envolvement"
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <FileUpload chooseImgUrl={setImgUrl} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Categories * (Max 3)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {validCategories.map((category) => (
                <label
                  key={category}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    categories.includes(category)
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={categories.includes(category)}
                    onChange={() => handleCategoryChange(category)}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">{category}</span>
                </label>
              ))}
            </div>
            {errors.categories && (
              <p className="text-red-500 text-sm mt-1">{errors.categories}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPricing = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Pricing & Availability
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="space-y-4"
            title="Paid envolvements are not available in early access."
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pricing
              </label>
              <div className="flex items-center space-x-4 cursor-not-allowed">
                <div className="flex-1">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      value={price}
                      readOnly
                      placeholder="0.00"
                      disabled
                      className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.price ? "border-red-500" : "border-gray-300"
                      } bg-gray-100`}
                    />
                  </div>
                  {errors.price && (
                    <p className="text-red-500 text-sm mt-1">{errors.price}</p>
                  )}
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked
                    readOnly
                    disabled
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">Free</span>
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(
                        Math.min(10000, parseInt(e.target.value) || 0)
                      )
                    }
                    max="10000"
                    placeholder="0"
                    disabled={unlimited}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.quantity ? "border-red-500" : "border-gray-300"
                    } ${unlimited ? "bg-gray-100" : ""}`}
                  />
                  {errors.quantity && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.quantity}
                    </p>
                  )}
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => setUnlimited(e.target.checked)}
                    className="mr-2 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">Unlimited</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDates = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Important Dates
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              min={today}
              max={
                targetEnvolvmentDate
                  ? new Date(targetEnvolvmentDate).toISOString().split("T")[0]
                  : ""
              }
              value={
                endDate ? new Date(endDate).toISOString().split("T")[0] : ""
              }
              onChange={(e) =>
                setEndDate(
                  e.target.value ? new Date(e.target.value + "T00:00:00") : null
                )
              }
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.endDate ? "border-red-500" : "border-gray-300"
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              When this envolvement will no longer be available
            </p>
            {errors.endDate && (
              <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Envolvement Date
            </label>
            <input
              type="date"
              min={
                endDate
                  ? new Date(endDate).toISOString().split("T")[0]
                  : today
              }
              value={
                targetEnvolvmentDate
                  ? new Date(targetEnvolvmentDate).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                setTargetEnvolvmentDate(
                  e.target.value ? new Date(e.target.value + "T00:00:00") : null
                )
              }
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.targetEnvolvmentDate
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              When the main activity will occur
            </p>
            {errors.targetEnvolvmentDate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.targetEnvolvmentDate}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Envolvement Settings
        </h3>

        <div className="space-y-6">
          {/* Submissions */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium text-gray-900">
                  User Submissions
                </h4>
                <p className="text-sm text-gray-600">
                  Allow users to submit content for this envolvement
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={submissions}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setSubmissions(isChecked);
                    if (!isChecked) {
                      setSubmissionDescription("");
                      setSubmissionDeadline(null);
                      setSubmissionContentPicture(false);
                      setSubmissionContentDescription(false);
                      setSubmissionContentUrl(false);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {submissions && (
              <div className="space-y-4 pl-4 border-l-2 border-primary-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Submission Description
                  </label>
                  <textarea
                    value={submissionDescription}
                    onChange={(e) => setSubmissionDescription(e.target.value)}
                    placeholder="Explain what users should submit"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Allowed Submission Types
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                      <input
                        type="checkbox"
                        checked={submissionContentDescription}
                        onChange={(e) =>
                          setSubmissionContentDescription(e.target.checked)
                        }
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">Text Description</span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                      <input
                        type="checkbox"
                        checked={submissionContentPicture}
                        onChange={(e) =>
                          setSubmissionContentPicture(e.target.checked)
                        }
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">Pictures</span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                      <input
                        type="checkbox"
                        checked={submissionContentUrl}
                        onChange={(e) =>
                          setSubmissionContentUrl(e.target.checked)
                        }
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">URLs</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Submission Deadline
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={
                      submissionDeadline
                        ? new Date(submissionDeadline)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setSubmissionDeadline(
                        e.target.value
                          ? new Date(e.target.value + "T00:00:00")
                          : null
                      )
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-medium text-gray-900">Comments</h4>
                <p className="text-sm text-gray-600">
                  Allow users to comment on this envolvement
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={comments}
                  onChange={(e) => setComments(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          {/* Published Content */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium text-gray-900">
                  Published Content
                </h4>
                <p className="text-sm text-gray-600">
                  This envolvement will result in published content
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            {published && (
              <div className="pl-4 border-l-2 border-primary-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Release Date
                </label>
                <input
                  type="date"
                  min={today}
                  value={
                    publishedReleaseDate
                      ? new Date(publishedReleaseDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setPublishedReleaseDate(
                      e.target.value
                        ? new Date(e.target.value + "T00:00:00")
                        : null
                    )
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderPricing();
      case 3:
        return renderDates();
      case 4:
        return renderSettings();
      default:
        return renderBasicInfo();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Authentication Required
          </h2>
          <p className="text-gray-600">
            Please log in to create an envolvement.
          </p>
        </div>
      </div>
    );
  }

  const previewData = {
    title: title || "Your Title",
    previewText: previewText || "Your preview text will appear here.",
    description: description || "Your detailed description will appear here.",
    imgUrl: imgUrl || "https://placehold.co/600x400?text=Your+Image",
    categories: categories,
    username: user?.nickname,
    userPicture: user?.picture,
    userID: user?.sub,
    price: { free: free, price: free ? 0 : price },
    quantity: {
      unlimited: unlimited,
      quantity: unlimited ? 0 : quantity,
      currentEnvolves: 0,
    },
    endDate: { endDate: endDate, none: !endDate },
    datePublished: new Date().toISOString(),
    _id: "preview-id", // Needed for links
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EnvolvementPreviewModal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          data={previewData}
        />

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create New Envolvement
          </h1>
          <p className="text-gray-600 mb-4">
            Share your unique experience with the world
          </p>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Form Container */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {renderStepContent()}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                currentStep === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Previous
            </button>

            <div className="flex items-center space-x-4">
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!canProceedToNext()}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    canProceedToNext()
                      ? "bg-primary-600 text-white hover:bg-primary-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isFormValid() || isSubmitting}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    isFormValid() && !isSubmitting
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </button>
              )}
            </div>
          </div>

          {/* Success Message */}
          {message && (
            <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {message}
                  </p>
                  {postedID && (
                    <Link
                      to={`/envolvement/${postedID}`}
                      className="text-sm text-green-600 hover:text-green-500 underline"
                    >
                      View your envolvement →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateEnvolvement;
